import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Add as AddIcon, Business, Phone, Email, Edit as EditIcon, Delete as DeleteIcon, Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { suppliersAPI } from '../../services/api';
import toast from 'react-hot-toast';

function Suppliers() {
  const { t } = useTranslation();
  const { tenantId } = useAuth();
  const [suppliers, setSuppliers] = useState([]);
  const [filteredSuppliers, setFilteredSuppliers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [filters, setFilters] = useState({
    name: '',
    cnpj: '',
    status: 'all'
  });
  const [formData, setFormData] = useState({
    name: '',
    cnpj: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    city: '',
    state: '',
    notes: '',
    status: 'active'
  });

  const loadSuppliers = useCallback(async () => {
    try {
      const response = await suppliersAPI.getAll();
      console.log('Suppliers API Response:', response.data);
      
      const suppliersData = Array.isArray(response?.data?.data) ? response.data.data : 
                           Array.isArray(response?.data) ? response.data : [];
      
      console.log('Suppliers with equipment count:', suppliersData.map(s => ({ 
        name: s.name, 
        equipment_count: s.equipment_count 
      })));
      
      setSuppliers(suppliersData);
      setFilteredSuppliers(suppliersData);
    } catch (error) {
      const currentTenant = tenantId;
      const mockSuppliers = [
        {
          id: '1',
          name: 'MedEquip Ltda',
          cnpj: '12.345.678/0001-90',
          contact_email: 'contato@medequip.com.br',
          contact_phone: '(11) 99999-9999',
          address: 'Rua das Flores, 123',
          city: 'São Paulo',
          state: 'SP',
          is_active: true,
          equipment_count: 15,
          last_contract: '2024-06-15',
          tenant_id: 'hospital-demo-001'
        },
        {
          id: '2',
          name: 'CardioTech Soluções',
          cnpj: '98.765.432/0001-10',
          contact_email: 'vendas@cardiotech.com.br',
          contact_phone: '(21) 88888-8888',
          address: 'Av. Paulista, 456',
          city: 'Rio de Janeiro',
          state: 'RJ',
          is_active: true,
          equipment_count: 8,
          last_contract: '2024-07-20',
          tenant_id: 'hospital-demo-001'
        },
        {
          id: '3',
          name: 'InfusaMed Equipamentos',
          cnpj: '11.222.333/0001-44',
          contact_email: 'suporte@infusamed.com.br',
          contact_phone: '(47) 77777-7777',
          address: 'Rua Industrial, 789',
          city: 'Blumenau',
          state: 'SC',
          is_active: false,
          equipment_count: 3,
          last_contract: '2024-01-10',
          tenant_id: 'hospital-demo-001'
        },
        {
          id: '4',
          name: 'Einstein Medical',
          cnpj: '55.666.777/0001-88',
          contact_email: 'contato@einstein-med.com.br',
          contact_phone: '(11) 55555-5555',
          address: 'Av. Einstein, 100',
          city: 'São Paulo',
          state: 'SP',
          is_active: true,
          equipment_count: 25,
          last_contract: '2024-08-01',
          tenant_id: 'hospital-einstein'
        }
      ].filter(supplier => supplier.tenant_id === currentTenant);
      setSuppliers(mockSuppliers);
      setFilteredSuppliers(mockSuppliers);
      console.warn('Suppliers API failed, using mock data:', error.message);
    }
  }, [tenantId]);

  useEffect(() => {
    loadSuppliers();
  }, [loadSuppliers]);

  const handleEdit = (supplier) => {
    setEditingSupplier(supplier);
    setFormData({
      name: supplier.name,
      cnpj: supplier.cnpj,
      contact_email: supplier.contact_email || supplier.email || '',
      contact_phone: supplier.contact_phone || supplier.phone || '',
      address: supplier.address,
      city: supplier.city,
      state: supplier.state,
      notes: supplier.notes || '',
      status: supplier.status || (supplier.is_active ? 'active' : 'inactive')
    });
    setOpen(true);
  };

  const handleDelete = async (supplierId) => {
    if (window.confirm('Tem certeza que deseja excluir este fornecedor?')) {
      try {
        await suppliersAPI.delete(supplierId);
        toast.success('Fornecedor excluído com sucesso!');
        // Recarrega a lista para garantir sincronização
        await loadSuppliers();
      } catch (error) {
        toast.error(error.response?.data?.message || 'Erro ao excluir fornecedor');
      }
    }
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setEditingSupplier(null);
    resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingSupplier) {
        await suppliersAPI.update(editingSupplier.id, formData);
        toast.success('Fornecedor atualizado com sucesso!');
      } else {
        const supplierData = { ...formData, tenant_id: tenantId };
        await suppliersAPI.create(supplierData);
        toast.success('Fornecedor criado com sucesso!');
      }
      handleCloseDialog();
      // Recarrega a lista para garantir sincronização
      await loadSuppliers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao salvar fornecedor');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      cnpj: '',
      contact_email: '',
      contact_phone: '',
      address: '',
      city: '',
      state: '',
      notes: '',
      status: 'active'
    });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleFilterChange = (field, value) => {
    const newFilters = { ...filters, [field]: value };
    setFilters(newFilters);
    applyFilters(newFilters);
  };

  const applyFilters = (currentFilters) => {
    let filtered = suppliers;

    if (currentFilters.name) {
      filtered = filtered.filter(supplier => 
        supplier.name.toLowerCase().includes(currentFilters.name.toLowerCase())
      );
    }

    if (currentFilters.cnpj) {
      filtered = filtered.filter(supplier => 
        supplier.cnpj.includes(currentFilters.cnpj)
      );
    }

    if (currentFilters.status !== 'all') {
      filtered = filtered.filter(supplier => {
        const supplierStatus = supplier.status || (supplier.is_active ? 'active' : 'inactive');
        return supplierStatus === currentFilters.status;
      });
    }

    setFilteredSuppliers(filtered);
  };

  const clearFilters = () => {
    setFilters({ name: '', cnpj: '', status: 'all' });
    setFilteredSuppliers(suppliers);
  };

  useEffect(() => {
    applyFilters(filters);
  }, [suppliers]);



  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          {t('suppliers.title')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpen(true)}
        >
          {t('suppliers.newSupplier')}
        </Button>
      </Box>

      {/* Filtros */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="Buscar por nome"
              value={filters.name}
              onChange={(e) => handleFilterChange('name', e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              size="small"
              label="Buscar por CNPJ"
              value={filters.cnpj}
              onChange={(e) => handleFilterChange('cnpj', e.target.value)}
              placeholder="00.000.000/0000-00"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                label="Status"
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="active">Ativos</MenuItem>
                <MenuItem value="inactive">Inativos</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={clearFilters}
              size="small"
            >
              Limpar
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Estatísticas */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Fornecedores
              </Typography>
              <Typography variant="h4">
                {filteredSuppliers.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Fornecedores Ativos
              </Typography>
              <Typography variant="h4" color="success.main">
                {filteredSuppliers.filter(s => s.status === 'active' || s.is_active).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Equipamentos Fornecidos
              </Typography>
              <Typography variant="h4">
                {filteredSuppliers.reduce((sum, s) => sum + (s.equipment_count || 0), 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabela de Fornecedores */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Fornecedor</TableCell>
              <TableCell>CNPJ</TableCell>
              <TableCell>Contato</TableCell>
              <TableCell>Equipamentos</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.isArray(filteredSuppliers) && filteredSuppliers.length > 0 ? filteredSuppliers.map((supplier) => (
              <TableRow key={supplier.id}>
                <TableCell>
                  <Box display="flex" alignItems="center">
                    <Business sx={{ mr: 1, color: 'primary.main' }} />
                    <Box>
                      <Typography variant="subtitle2">{supplier.name}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {supplier.email || supplier.contact_email}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontFamily="monospace">
                    {supplier.cnpj}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box>
                    <Box display="flex" alignItems="center" mb={0.5}>
                      <Email sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                      <Typography variant="caption">
                        {supplier.email || supplier.contact_email || 'Não informado'}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center">
                      <Phone sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                      <Typography variant="caption">
                        {supplier.phone || supplier.contact_phone || 'Não informado'}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={`${supplier.equipment_count || 0} equipamentos`}
                    color={(supplier.equipment_count || 0) > 0 ? 'primary' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={(supplier.status === 'active' || supplier.is_active) ? 'Ativo' : 'Inativo'}
                    color={(supplier.status === 'active' || supplier.is_active) ? 'success' : 'error'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => handleEdit(supplier)}
                    sx={{ mr: 1 }}
                  >
                    Editar
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => handleDelete(supplier.id)}
                  >
                    Excluir
                  </Button>
                </TableCell>
              </TableRow>
            )) : (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" color="textSecondary">
                    {suppliers.length === 0 ? 'Nenhum fornecedor cadastrado' : 'Nenhum fornecedor encontrado com os filtros aplicados'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog para Novo Fornecedor */}
      <Dialog open={open} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingSupplier ? 'Editar Fornecedor' : 'Novo Fornecedor'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={8}>
                <TextField
                  name="name"
                  label="Nome da Empresa"
                  fullWidth
                  required
                  value={formData.name}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  name="cnpj"
                  label="CNPJ"
                  fullWidth
                  required
                  value={formData.cnpj}
                  onChange={handleChange}
                  placeholder="00.000.000/0000-00"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="contact_email"
                  label="Email de Contato"
                  type="email"
                  fullWidth
                  required
                  value={formData.contact_email}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="contact_phone"
                  label="Telefone"
                  fullWidth
                  required
                  value={formData.contact_phone}
                  onChange={handleChange}
                  placeholder="(00) 00000-0000"
                />
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField
                  name="address"
                  label="Endereço"
                  fullWidth
                  value={formData.address}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  name="city"
                  label="Cidade"
                  fullWidth
                  value={formData.city}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={1}>
                <TextField
                  name="state"
                  label="UF"
                  fullWidth
                  value={formData.state}
                  onChange={handleChange}
                  inputProps={{ maxLength: 2 }}
                />
              </Grid>
              {editingSupplier && (
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      name="status"
                      value={formData.status}
                      label="Status"
                      onChange={handleChange}
                    >
                      <MenuItem value="active">Ativo</MenuItem>
                      <MenuItem value="inactive">Inativo</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField
                  name="notes"
                  label="Observações"
                  fullWidth
                  multiline
                  rows={3}
                  value={formData.notes}
                  onChange={handleChange}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancelar</Button>
            <Button type="submit" variant="contained">
              {editingSupplier ? 'Atualizar' : 'Salvar'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}

export default Suppliers;